import { useState, type FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  FolderPlus,
  Target,
  Beaker,
  Stethoscope,
  ShieldCheck,
  Atom,
  FolderKanban,
  Calendar,
  Plus,
  FileText,
} from 'lucide-react';
import { api, extractApiError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { formatDate, cn } from '@/lib/utils';

type ProjectType = 'REGULATORY_SUBMISSION' | 'CLINICAL_TRIAL' | 'PHARMACOVIGILANCE' | 'COMPLIANCE_PROGRAMME' | 'CMC_PROGRAMME' | 'OTHER';

const TYPE_META: Record<ProjectType, { label: string; icon: React.ComponentType<{ className?: string }>; tone: string }> = {
  REGULATORY_SUBMISSION: { label: 'Regulatory submission', icon: Target, tone: 'bg-indigo-500/10 text-indigo-600' },
  CLINICAL_TRIAL: { label: 'Clinical trial', icon: Beaker, tone: 'bg-blue-500/10 text-blue-600' },
  PHARMACOVIGILANCE: { label: 'Pharmacovigilance', icon: Stethoscope, tone: 'bg-rose-500/10 text-rose-600' },
  COMPLIANCE_PROGRAMME: { label: 'Compliance', icon: ShieldCheck, tone: 'bg-teal-500/10 text-teal-600' },
  CMC_PROGRAMME: { label: 'CMC programme', icon: Atom, tone: 'bg-violet-500/10 text-violet-600' },
  OTHER: { label: 'Other', tone: 'bg-muted text-muted-foreground', icon: FolderKanban },
};

interface ProjectRow {
  id: string;
  name: string;
  slug: string;
  type: ProjectType;
  status: string;
  productName: string | null;
  targetMarkets: string[];
  targetSubmissionDate: string | null;
  createdAt: string;
  _count: { documents: number; workflowRuns: number };
}

export function ProjectsPage() {
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);

  const projects = useQuery({
    queryKey: ['projects'],
    queryFn: async () => (await api.get('/api/projects')).data,
  });

  const create = useMutation({
    mutationFn: async (body: Record<string, unknown>) => (await api.post('/api/projects', body)).data,
    onSuccess: () => {
      toast.success('Project created');
      setCreateOpen(false);
      qc.invalidateQueries({ queryKey: ['projects'] });
    },
    onError: (err) => toast.error('Failed to create', extractApiError(err).message),
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl">Projects</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Organise submissions, clinical trials, PV programmes and compliance work. Documents,
            workflows and team assignments nest inside a project.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-1.5 h-4 w-4" /> New project
        </Button>
      </div>

      {projects.isLoading ? (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-40" />)}
        </div>
      ) : !projects.data?.items?.length ? (
        <EmptyState
          icon={FolderPlus}
          title="No projects yet"
          description="Create your first project to get started. A project is the top-level container that holds documents, workflows, and team assignments."
          action={{ label: 'New project', onClick: () => setCreateOpen(true) }}
        />
      ) : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {(projects.data.items as ProjectRow[]).map((p) => {
            const meta = TYPE_META[p.type] ?? TYPE_META.OTHER;
            const Icon = meta.icon;
            const targetDate = p.targetSubmissionDate ? new Date(p.targetSubmissionDate) : null;
            const daysRemaining = targetDate ? Math.ceil((targetDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24)) : null;
            const overdue = daysRemaining !== null && daysRemaining < 0;
            return (
              <Link key={p.id} to={`/projects/${p.id}`} className="group block rounded-lg border bg-card p-4 transition-all hover:border-primary/40 hover:shadow-md">
                <div className="flex items-start gap-3">
                  <div className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-md', meta.tone)}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium group-hover:text-primary">{p.name}</div>
                    <div className="mt-0.5 text-[11px] text-muted-foreground">
                      {meta.label}
                      {p.productName && ` · ${p.productName}`}
                    </div>
                  </div>
                  <Badge variant={p.status === 'active' ? 'success' : p.status === 'on_hold' ? 'warning' : 'secondary'}>
                    {p.status}
                  </Badge>
                </div>
                <div className="mt-4 grid grid-cols-3 gap-2 border-t pt-3 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <FileText className="h-3 w-3" /> {p._count.documents} docs
                  </div>
                  <div>{p._count.workflowRuns} runs</div>
                  <div className={cn('text-right', overdue && 'text-destructive')}>
                    {targetDate ? (
                      <span className="flex items-center justify-end gap-1">
                        <Calendar className="h-3 w-3" />
                        {overdue ? `${Math.abs(daysRemaining!)}d overdue` : daysRemaining !== null ? `${daysRemaining}d left` : formatDate(targetDate)}
                      </span>
                    ) : (
                      '—'
                    )}
                  </div>
                </div>
                {p.targetMarkets?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {p.targetMarkets.map((m) => (
                      <span key={m} className="rounded bg-muted/60 px-1.5 py-0.5 text-[10px]">
                        {m}
                      </span>
                    ))}
                  </div>
                )}
              </Link>
            );
          })}
        </div>
      )}

      {createOpen && <CreateProjectDialog onClose={() => setCreateOpen(false)} onSubmit={(b) => create.mutate(b)} busy={create.isPending} />}
    </div>
  );
}

function CreateProjectDialog({ onClose, onSubmit, busy }: { onClose: () => void; onSubmit: (body: Record<string, unknown>) => void; busy: boolean }) {
  const [form, setForm] = useState({
    name: '',
    type: 'REGULATORY_SUBMISSION' as ProjectType,
    productName: '',
    targetMarkets: 'UK, EU',
    targetSubmissionDate: '',
    description: '',
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    onSubmit({
      name: form.name,
      type: form.type,
      productName: form.productName || undefined,
      targetMarkets: form.targetMarkets ? form.targetMarkets.split(',').map((s) => s.trim()).filter(Boolean) : [],
      targetSubmissionDate: form.targetSubmissionDate ? new Date(form.targetSubmissionDate).toISOString() : undefined,
      description: form.description || undefined,
    });
  }

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>New project</DialogTitle>
          <DialogDescription>
            Start a new submission, trial, PV programme, or compliance effort.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Project name</Label>
            <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Drug X MAA submission" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Type</Label>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value as ProjectType })} className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
                {Object.entries(TYPE_META).map(([k, v]) => (<option key={k} value={k}>{v.label}</option>))}
              </select>
            </div>
            <div>
              <Label>Product / drug name (optional)</Label>
              <Input value={form.productName} onChange={(e) => setForm({ ...form, productName: e.target.value })} placeholder="TestDrug 100mg" />
            </div>
            <div>
              <Label>Target markets</Label>
              <Input value={form.targetMarkets} onChange={(e) => setForm({ ...form, targetMarkets: e.target.value })} placeholder="UK, EU, US" />
            </div>
            <div>
              <Label>Target submission date (optional)</Label>
              <Input type="date" value={form.targetSubmissionDate} onChange={(e) => setForm({ ...form, targetSubmissionDate: e.target.value })} />
            </div>
          </div>
          <div>
            <Label>Description (optional)</Label>
            <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={busy}>{busy ? 'Creating...' : 'Create project'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
